import { ethers } from 'ethers';

// 扩展Window接口以包含ethereum
declare global {
  interface Window {
    ethereum?: any;
  }
}

export interface WalletInfo {
  address: string;
  chainId: string;
  networkName: string;
  connectedAt: number;
  provider: string; // 'metamask' | 'coinbase' | 'trust' | 'other'
}

export interface WalletSession {
  isConnected: boolean;
  walletInfo: WalletInfo | null;
  sessionId: string;
  lastActivity: number;
}

class WalletManager {
  private static instance: WalletManager;
  private session: WalletSession | null = null;
  private sessionId: string = '';
  private readonly SESSION_KEY = 'wallet-session';
  private readonly SESSION_TIMEOUT = 24 * 60 * 60 * 1000; // 24小时

  private constructor() {
    this.sessionId = this.generateSessionId();
    this.loadSession();
    this.setupEventListeners();
  }

  public static getInstance(): WalletManager {
    if (!WalletManager.instance) {
      WalletManager.instance = new WalletManager();
    }
    return WalletManager.instance;
  }

  // 生成会话ID
  private generateSessionId(): string {
    return `wallet_session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  // 加载会话
  private loadSession(): void {
    try {
      const stored = sessionStorage.getItem(this.SESSION_KEY);
      if (stored) {
        const session: WalletSession = JSON.parse(stored);
        
        // 检查会话是否过期
        if (Date.now() - session.lastActivity < this.SESSION_TIMEOUT) {
          this.session = session;
          console.log('Wallet session restored:', session);
        } else {
          console.log('Wallet session expired, clearing...');
          this.clearSession();
        }
      }
    } catch (error) {
      console.error('Error loading wallet session:', error);
      this.clearSession();
    }
  }

  // 保存会话
  private saveSession(): void {
    if (this.session) {
      this.session.lastActivity = Date.now();
      sessionStorage.setItem(this.SESSION_KEY, JSON.stringify(this.session));
    }
  }

  // 清除会话
  private clearSession(): void {
    this.session = null;
    sessionStorage.removeItem(this.SESSION_KEY);
  }

  // Set event listeners
  private setupEventListeners(): void {
    if (typeof window !== 'undefined' && window.ethereum) {
      const ethereumProvider = this.getEthereumProvider();
      if (!ethereumProvider) return;

      // Listen for account changes
      ethereumProvider.on('accountsChanged', (accounts: string[]) => {
        console.log('Accounts changed:', accounts);
        if (accounts.length === 0) {
          this.disconnect();
          this.notifyStatusChange();
        } else {
          this.updateWalletInfo();
          this.notifyStatusChange();
        }
      });

      // Listen for network changes
      ethereumProvider.on('chainChanged', (chainId: string) => {
        console.log('Chain changed:', chainId);
        this.updateWalletInfo();
        this.notifyStatusChange();
      });

      // Listen for connection
      ethereumProvider.on('connect', (connectInfo: any) => {
        console.log('Wallet connected:', connectInfo);
        this.updateWalletInfo();
        this.notifyStatusChange();
      });

      // Listen for disconnection
      ethereumProvider.on('disconnect', (error: any) => {
        console.log('Wallet disconnected:', error);
        this.disconnect();
        this.notifyStatusChange();
      });
    }
  }

  // 通知状态变化
  private notifyStatusChange(): void {
    // 触发自定义事件，通知其他组件状态变化
    const event = new CustomEvent('walletStatusChanged', {
      detail: {
        isConnected: this.session?.isConnected || false,
        walletInfo: this.session?.walletInfo || null
      }
    });
    window.dispatchEvent(event);
  }

  // 检查是否已连接 - 实时验证钱包状态
  public async isConnected(): Promise<boolean> {
    if (!this.session?.isConnected || !this.session?.walletInfo) {
      return false;
    }
    
    try {
      // Real-time verify wallet account status
      const provider = this.getEthereumProvider();
      if (!provider) {
        this.disconnect();
        return false;
      }
      
      const accounts = await provider.request({ method: 'eth_accounts' });
      if (accounts.length === 0) {
        this.disconnect();
        return false;
      }
      
      // 验证当前账户是否匹配
      const currentAccount = accounts[0];
      if (this.session.walletInfo.address !== currentAccount) {
        this.disconnect();
        return false;
      }
      
      return true;
    } catch (error) {
      console.error('Wallet connection verification failed:', error);
      this.disconnect();
      return false;
    }
  }

  // 获取钱包信息
  public getWalletInfo(): WalletInfo | null {
    return this.session?.walletInfo || null;
  }

  // 获取会话信息
  public getSession(): WalletSession | null {
    return this.session;
  }

  // Get the primary Ethereum provider
  private getEthereumProvider(): any {
    if (window.ethereum) {
      console.log('Available providers:', {
        isMetaMask: window.ethereum.isMetaMask,
        isBinance: window.ethereum.isBinance,
        providers: window.ethereum.providers,
        names: window.ethereum.name,
        walletName: window.ethereum.walletName
      });
      
      // Check if it's an array of providers (some browsers have multiple)
      if (Array.isArray(window.ethereum.providers)) {
        console.log('Multiple providers detected:', window.ethereum.providers.map((p: any) => ({
          name: p.name,
          walletName: p.walletName,
          isBinance: p.isBinance,
          isMetaMask: p.isMetaMask,
          id: p.id
        })));
        
        // Try to find Binance wallet first - check all possible identifiers
        const binanceWallet = window.ethereum.providers.find((p: any) => 
          p.isBinance || 
          p.name?.toLowerCase().includes('binance') ||
          p.walletName?.toLowerCase().includes('binance') ||
          p.id?.toLowerCase().includes('binance') ||
          p.toString().includes('Binance')
        );
        
        if (binanceWallet) {
          console.log('Found Binance Wallet provider, using it:', binanceWallet);
          return binanceWallet;
        }
        
        console.log('Binance Wallet not found in providers, checking if window.ethereum is Binance');
        // Check if the main ethereum object is Binance
        if (window.ethereum.toString().includes('Binance') || 
            window.ethereum.name?.toLowerCase().includes('binance') ||
            window.ethereum.walletName?.toLowerCase().includes('binance')) {
          console.log('Using main ethereum object as Binance wallet');
          return window.ethereum;
        }
        
        return window.ethereum.providers[0] || window.ethereum;
      }
      
      // If no providers array, check if main ethereum object is Binance
      if (window.ethereum.toString().includes('Binance') || 
          window.ethereum.name?.toLowerCase().includes('binance') ||
          window.ethereum.walletName?.toLowerCase().includes('binance')) {
        console.log('Main ethereum object is Binance wallet');
        return window.ethereum;
      }
      
      return window.ethereum;
    }
    return null;
  }

  // 连接钱包
  public async connectWallet(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    try {
      const provider = this.getEthereumProvider();
      if (!provider) {
        return { success: false, error: 'Please install MetaMask or another Web3 wallet' };
      }

      console.log('Available wallet provider:', provider);

      console.log('Starting wallet connection...');
      
      // First check if there are already connected accounts
      let accounts: string[] = [];
      try {
        const existingAccounts = await provider.request({ method: 'eth_accounts' });
        if (existingAccounts && existingAccounts.length > 0) {
          console.log('Found connected accounts:', existingAccounts);
          accounts = existingAccounts;
        } else {
          console.log('No connected accounts, requesting new connection...');
          // Request account access, reduce timeout to 15 seconds
          console.log('Requesting wallet authorization...');
          console.log('Provider being used:', {
            name: provider.name || provider.walletName,
            isBinance: provider.isBinance,
            isMetaMask: provider.isMetaMask
          });
          accounts = await Promise.race([
            provider.request({ method: 'eth_requestAccounts' }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Connection timeout, please try again')), 15000)
            )
          ]) as string[];
          console.log('Got accounts from provider:', accounts);
        }
      } catch (error: any) {
        console.log('Error checking existing accounts, trying to request new connection:', error);
        // If user rejected, don't show timeout error
        if (error.code === 4001 || error.message?.includes('User rejected')) {
          return { success: false, error: 'Please click "Connect" in your wallet popup to connect.' };
        }
        // Request account access
        console.log('Requesting wallet authorization...');
        try {
          accounts = await Promise.race([
            provider.request({ method: 'eth_requestAccounts' }),
            new Promise((_, reject) => 
              setTimeout(() => reject(new Error('Connection timeout, please try again')), 15000)
            )
          ]) as string[];
        } catch (requestError: any) {
        if (requestError.code === 4001 || requestError.message?.includes('User rejected')) {
          return { success: false, error: 'Please click "Connect" in your wallet popup to connect.' };
        }
          throw requestError;
        }
      }
      
      console.log('Got accounts:', accounts);
      
      if (!accounts || accounts.length === 0) {
        return { success: false, error: 'Please click "Connect" in your wallet popup to connect.' };
      }

      // Get network info
      const ethersProvider = new ethers.BrowserProvider(provider);
      const network = await ethersProvider.getNetwork();
      const chainId = network.chainId.toString();

      // Detect wallet type
      const providerType = this.detectProviderType();

      const walletInfo: WalletInfo = {
        address: accounts[0],
        chainId: chainId,
        networkName: network.name || 'Unknown',
        connectedAt: Date.now(),
        provider: providerType,
      };

      // Create session
      this.session = {
        isConnected: true,
        walletInfo: walletInfo,
        sessionId: this.sessionId,
        lastActivity: Date.now(),
      };

      this.saveSession();
      console.log('Wallet connected successfully:', walletInfo);

      // Notify status change
      this.notifyStatusChange();

      return { success: true, walletInfo };
    } catch (error: any) {
      console.error('Wallet connection failed:', error);
      return { success: false, error: error.message || 'Failed to connect wallet' };
    }
  }

  // Auto-connect (if previously connected)
  public async autoConnect(): Promise<{ success: boolean; walletInfo?: WalletInfo; error?: string }> {
    try {
      if (!this.isConnected()) {
        return { success: false, error: 'No active session' };
      }

      const provider = this.getEthereumProvider();
      if (!provider) {
        return { success: false, error: 'Web3 wallet not available' };
      }

      // Check if accounts are still available
      const accounts = await provider.request({ method: 'eth_accounts' });
      
      if (accounts.length === 0) {
        this.disconnect();
        return { success: false, error: 'Wallet not connected' };
      }

      // Update wallet info
      await this.updateWalletInfo();
      
      if (this.session?.walletInfo) {
        console.log('Wallet auto-connected:', this.session.walletInfo);
        return { success: true, walletInfo: this.session.walletInfo };
      }

      return { success: false, error: 'Failed to update wallet info' };
    } catch (error: any) {
      console.error('Auto-connect failed:', error);
      this.disconnect();
      return { success: false, error: error.message || 'Auto-connect failed' };
    }
  }

  // Update wallet info
  private async updateWalletInfo(): Promise<void> {
    if (!this.session?.walletInfo) return;

    const provider = this.getEthereumProvider();
    if (!provider) return;

    try {
      const ethersProvider = new ethers.BrowserProvider(provider);
      const network = await ethersProvider.getNetwork();
      
      this.session.walletInfo.chainId = network.chainId.toString();
      this.session.walletInfo.networkName = network.name || 'Unknown';
      this.session.lastActivity = Date.now();
      
      this.saveSession();
    } catch (error) {
      console.error('Error updating wallet info:', error);
    }
  }

  // Detect wallet type
  private detectProviderType(): string {
    if (!window.ethereum) return 'unknown';

    console.log('Detecting wallet type, ethereum object:', window.ethereum);
    console.log('Wallet properties:', {
      isMetaMask: window.ethereum.isMetaMask,
      isCoinbaseWallet: window.ethereum.isCoinbaseWallet,
      isTrust: window.ethereum.isTrust,
      isBinance: window.ethereum.isBinance,
      isImToken: window.ethereum.isImToken,
      isBitgetWallet: window.ethereum.isBitgetWallet,
      isOkxWallet: window.ethereum.isOkxWallet,
      isTokenPocket: window.ethereum.isTokenPocket,
      isRainbow: window.ethereum.isRainbow,
      isBraveWallet: window.ethereum.isBraveWallet,
      isPhantom: window.ethereum.isPhantom,
    });

    // Check wallet provider identifier
    const providerInfo = window.ethereum.providerInfo || window.ethereum.providers;
    console.log('Provider info:', providerInfo);

    // Detect by wallet name
    const walletName = window.ethereum.walletName || window.ethereum.name;
    console.log('Wallet name:', walletName);

    if (walletName) {
      const name = walletName.toLowerCase();
      if (name.includes('binance')) return 'binance';
      if (name.includes('coinbase')) return 'coinbase';
      if (name.includes('trust')) return 'trust';
      if (name.includes('imtoken')) return 'imtoken';
      if (name.includes('bitget')) return 'bitget';
      if (name.includes('okx')) return 'okx';
      if (name.includes('tokenpocket')) return 'tokenpocket';
      if (name.includes('rainbow')) return 'rainbow';
      if (name.includes('brave')) return 'brave';
      if (name.includes('phantom')) return 'phantom';
      if (name.includes('metamask')) return 'metamask';
    }

    // Detect by specific properties
    if (window.ethereum.isMetaMask && !window.ethereum.isBraveWallet) return 'metamask';
    if (window.ethereum.isCoinbaseWallet) return 'coinbase';
    if (window.ethereum.isTrust) return 'trust';
    if (window.ethereum.isBinance) return 'binance';
    if (window.ethereum.isImToken) return 'imtoken';
    if (window.ethereum.isBitgetWallet) return 'bitget';
    if (window.ethereum.isOkxWallet) return 'okx';
    if (window.ethereum.isTokenPocket) return 'tokenpocket';
    if (window.ethereum.isRainbow) return 'rainbow';
    if (window.ethereum.isBraveWallet) return 'brave';
    if (window.ethereum.isPhantom) return 'phantom';
    
    // Detect by user agent
    const userAgent = navigator.userAgent.toLowerCase();
    if (userAgent.includes('binance')) return 'binance';
    if (userAgent.includes('coinbase')) return 'coinbase';
    if (userAgent.includes('trust')) return 'trust';
    if (userAgent.includes('imtoken')) return 'imtoken';
    if (userAgent.includes('bitget')) return 'bitget';
    if (userAgent.includes('okx')) return 'okx';
    if (userAgent.includes('tokenpocket')) return 'tokenpocket';
    if (userAgent.includes('rainbow')) return 'rainbow';
    if (userAgent.includes('brave')) return 'brave';
    if (userAgent.includes('phantom')) return 'phantom';
    
    // Detect by domain (if in iframe)
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname.toLowerCase();
      if (hostname.includes('binance')) return 'binance';
      if (hostname.includes('coinbase')) return 'coinbase';
      if (hostname.includes('trust')) return 'trust';
    }
    
    console.log('Unrecognized wallet type, using default');
    return 'other';
  }

  // Disconnect
  public disconnect(): void {
    console.log('Disconnecting wallet...');
    this.clearSession();
    this.notifyStatusChange();
  }

  // Get connection status text
  public getConnectionStatus(): string {
    if (!this.isConnected()) {
      return 'Not Connected';
    }

    const walletInfo = this.getWalletInfo();
    if (!walletInfo) {
      return 'Connection Status Unknown';
    }

    const timeConnected = Date.now() - walletInfo.connectedAt;
    const hoursConnected = Math.floor(timeConnected / (1000 * 60 * 60));
    
    if (hoursConnected < 1) {
      return 'Just Connected';
    } else if (hoursConnected < 24) {
      return `Connected for ${hoursConnected} hours`;
    } else {
      const daysConnected = Math.floor(hoursConnected / 24);
      return `Connected for ${daysConnected} days`;
    }
  }

  // Get short form of wallet address
  public getShortAddress(): string {
    const walletInfo = this.getWalletInfo();
    if (!walletInfo) return '';
    
    return `${walletInfo.address.slice(0, 6)}...${walletInfo.address.slice(-4)}`;
  }

  // Check if session is active
  public isSessionActive(): boolean {
    if (!this.session) return false;
    
    const timeSinceLastActivity = Date.now() - this.session.lastActivity;
    return timeSinceLastActivity < this.SESSION_TIMEOUT;
  }

  // Refresh session activity time
  public refreshActivity(): void {
    if (this.session) {
      this.session.lastActivity = Date.now();
      this.saveSession();
    }
  }

  // Get session statistics
  public getSessionStats(): {
    isConnected: boolean;
    connectedTime: string;
    sessionDuration: string;
    provider: string;
    network: string;
  } {
    const isConnected = this.isConnected();
    const walletInfo = this.getWalletInfo();
    
    if (!isConnected || !walletInfo) {
      return {
        isConnected: false,
        connectedTime: '',
        sessionDuration: '',
        provider: '',
        network: '',
      };
    }

    const connectedTime = new Date(walletInfo.connectedAt).toLocaleString();
    const sessionDuration = this.getConnectionStatus();
    
    return {
      isConnected: true,
      connectedTime,
      sessionDuration,
      provider: walletInfo.provider,
      network: walletInfo.networkName,
    };
  }
}

// 导出单例实例
export const walletManager = WalletManager.getInstance();
